import React, { useCallback, useEffect, useState } from 'react';
import { getSystemStatus } from '../../api/system/system.api';
import LoadingBlock from '../LoadingBlock';

/**
 * 배포 버전 확인(`/sysadmin/status`) — 2026-09-16 신설.
 *
 * ## 왜 있는가
 *
 * **운영에 지금 어느 코드가 돌고 있는지 확인할 방법이 없었다.**
 * 이미지 태그가 `:prod` 고정이라 배포를 몇 번 하든 같은 문자열을 덮어쓰고, ArgoCD 가 보여 주는
 * revision 은 `headless-configuration` 저장소의 커밋이지 앱 코드가 아니다.
 * 그래서 "배포했는데 안 바뀐 것 같다" 를 확인하려면 파드 imageID 의 digest → 레지스트리 푸시
 * 시각 → GitHub Actions 이력을 대조하는 **추정**밖에 없었다.
 * 조사 = `_ai-runs/2026-09-16_001_automate_message_usage_queries/007_…md` §2
 *
 * 🔑 이 화면은 **배포 파이프라인을 전혀 건드리지 않고** 같은 질문에 답한다.
 *    백엔드는 자기 커밋을 jar 안에 굽고(`:generateBuildInfo`), 이 화면이 그것을 읽는다.
 *
 * ## 🔴 프론트엔드는 커밋을 알 수 없다 (의도된 한계)
 *
 * 프론트 빌드(`npm run build`)는 **도커 이미지 안에서** 돌고 그 안에는 `git` 도 `.git` 도 없다.
 * 커밋 해시를 넣으려면 워크플로우에 값을 주입해야 하는데, 그건 배포 파이프라인 변경이다.
 * ⇒ 대신 **지금 이 브라우저가 실제로 내려받은 번들 파일명**을 보여준다. CRA 가 파일명에 넣는
 *    해시는 **내용이 바뀌면 반드시 바뀌는** 값이라, 커밋을 알려주지는 못해도
 *    **"프론트가 실제로 교체되었는가"** 라는 질문에는 답한다(배포 전후로 값을 비교).
 *    `Last-Modified` 는 그 파일이 이미지 빌드 때 만들어진 시각이다. `[INFERENCE]`
 *
 * ⚠️ 이 값은 **이 브라우저가 캐시에서 읽었을 수도 있다.** 값이 그대로면 강력 새로고침으로
 *    한 번 더 확인할 것. (그래서 아래에 그 안내를 적어 둔다.)
 */

/** 한 줄 = 라벨 + 값. 값은 길어도 접히지 않게 `break-all`. */
const Row = ({ label, children, hint }) => (
  <div className="flex flex-col gap-0.5 border-b border-gray-100 py-2 sm:flex-row sm:items-baseline sm:gap-4">
    <div className="w-40 shrink-0 text-xs font-semibold text-gray-500">{label}</div>
    <div className="min-w-0 flex-1 break-all font-mono text-sm text-gray-800">{children}</div>
    {hint && <div className="text-xs text-gray-400 sm:w-64 sm:shrink-0">{hint}</div>}
  </div>
);

const CopyButton = ({ value }) => {
  const [copied, setCopied] = useState(false);
  if (!value || value === 'unknown') return null;
  return (
    <button
      type="button"
      className="ml-2 rounded border border-gray-200 px-1.5 py-0.5 align-middle text-[11px] text-gray-500 hover:bg-gray-50"
      onClick={() => {
        // 클립보드는 https·사용자 제스처가 있어야 동작한다. 실패해도 화면이 깨지면 안 된다.
        Promise.resolve(navigator.clipboard?.writeText(value))
          .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); })
          .catch(() => {});
      }}
    >
      {copied ? '복사됨' : '복사'}
    </button>
  );
};

/**
 * 지금 이 페이지가 로드한 메인 번들의 경로.
 *
 * CRA 는 `static/js/main.<해시>.js` 로 낸다. `<script>` 태그를 먼저 보고, 못 찾으면
 * 실제로 받은 리소스 목록(Performance API)에서 찾는다 — 두 경로 다 실패하면 `null` 이고
 * 화면은 "확인할 수 없음" 으로 정직하게 표시한다.
 */
const findMainBundleUrl = () => {
  const tag = document.querySelector('script[src*="/static/js/main."]');
  if (tag?.src) return tag.src;
  const entries = performance.getEntriesByType?.('resource') ?? [];
  return entries.map((e) => e.name).find((name) => /\/static\/js\/main\.[^/]+\.js/.test(name)) ?? null;
};

export default function SystemStatusAdmin() {
  const [backend, setBackend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [frontend, setFrontend] = useState({ url: null, builtAt: '' });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setBackend(await getSystemStatus());
    } catch (e) {
      setBackend(null);
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const url = findMainBundleUrl();
    setFrontend({ url, builtAt: '' });
    if (!url) return;
    // HEAD 로 헤더만 받는다(번들을 다시 내려받지 않는다). 실패하면 시각만 비운다 — 파일명은 이미 유효한 값이다.
    fetch(url, { method: 'HEAD', cache: 'no-store' })
      .then((res) => setFrontend({ url, builtAt: res.headers.get('last-modified') || '' }))
      .catch(() => {});
  }, []);

  const fileName = frontend.url ? frontend.url.split('/').pop() : null;
  // "main.abc12345.js" → "abc12345". 형식이 다르면 파일명을 그대로 보여준다(추측하지 않는다).
  const bundleHash = fileName?.match(/^main\.([^.]+)\.js$/)?.[1] ?? fileName;

  return (
    <div className="p-6">
      <div className="mb-1 flex items-center gap-3">
        <h2 className="text-lg font-bold text-gray-800">배포 버전</h2>
        <button
          type="button"
          className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
          onClick={load}
          disabled={loading}
        >
          다시 조회
        </button>
      </div>
      <p className="mb-6 text-xs leading-relaxed text-gray-500">
        운영에 실제로 떠 있는 코드의 버전입니다. 배포 직후 <b>배포했다고 생각한 커밋과 다르면 그 배포는 반영되지 않은 것</b>입니다.
      </p>

      {/* ── 백엔드 ───────────────────────────────────────────── */}
      <h3 className="mb-2 text-sm font-bold text-gray-700">백엔드 (headless-redirect-service)</h3>
      {loading || error || !backend ? (
        <LoadingBlock
          text="버전 정보를 불러오는 중입니다…"
          error={error}
          title="버전 정보를 불러오지 못했습니다."
        />
      ) : (
        <div className="mb-8 rounded-lg border border-gray-200 px-4 py-2">
          <Row label="커밋" hint="git log 에서 이 값으로 찾을 수 있습니다">
            <span className="font-bold">{backend.shortCommit}</span>
            <CopyButton value={backend.commit} />
            <div className="mt-0.5 text-[11px] text-gray-400">{backend.commit}</div>
          </Row>
          <Row label="브랜치" hint="CI 는 detached HEAD 라 'HEAD' 로 나올 수 있습니다">{backend.branch}</Row>
          <Row label="커밋 시각" hint="코드가 커밋된 시각 (KST)">{backend.commitTime}</Row>
          <Row label="빌드 시각" hint="이 jar 가 만들어진 시각 (KST)">{backend.buildTime}</Row>
          <Row label="파드 기동 시각" hint="이 값이 최근이면 방금 재시작한 것입니다">{backend.startedAt}</Row>
          <Row label="기동 후 경과">
            {Math.floor(backend.uptimeSeconds / 86400)}일{' '}
            {Math.floor((backend.uptimeSeconds % 86400) / 3600)}시간{' '}
            {Math.floor((backend.uptimeSeconds % 3600) / 60)}분
          </Row>
          <Row label="서버 시각" hint="브라우저 시각과 크게 다르면 시간대 문제를 의심하세요">{backend.serverTime}</Row>
          <Row label="프로파일">{(backend.activeProfiles ?? []).join(', ') || '(없음)'}</Row>
          <Row label="빌드 상태">
            {backend.dirty ? (
              <span className="font-bold text-red-600">
                커밋되지 않은 변경이 포함된 빌드입니다 (dirty)
              </span>
            ) : (
              <span className="text-gray-700">커밋 그대로 빌드됨 (clean)</span>
            )}
          </Row>
          {backend.commit === 'unknown' && (
            <p className="py-2 text-xs text-amber-600">
              값이 <code>unknown</code> 입니다 — 이 기능이 들어가기 <b>전에</b> 빌드된 이미지가 돌고 있거나,
              빌드 시점에 git 정보를 읽지 못한 경우입니다.
            </p>
          )}
        </div>
      )}

      {/* ── 프론트엔드 ───────────────────────────────────────── */}
      <h3 className="mb-2 text-sm font-bold text-gray-700">프론트엔드 (headless-qr-offline)</h3>
      <div className="rounded-lg border border-gray-200 px-4 py-2">
        <Row label="번들 식별자" hint="내용이 바뀌면 반드시 바뀝니다">
          {bundleHash ? (
            <>
              <span className="font-bold">{bundleHash}</span>
              <CopyButton value={fileName} />
            </>
          ) : (
            <span className="text-gray-400">확인할 수 없음</span>
          )}
        </Row>
        <Row label="번들 파일" hint="지금 이 브라우저가 내려받은 파일">
          {fileName ?? <span className="text-gray-400">확인할 수 없음</span>}
        </Row>
        <Row label="번들 생성 시각" hint="이미지 빌드 시각 (서버 헤더 기준, UTC 표기일 수 있음)">
          {frontend.builtAt || <span className="text-gray-400">확인할 수 없음</span>}
        </Row>
      </div>
      <p className="mt-2 text-xs leading-relaxed text-gray-500">
        🔴 프론트엔드는 <b>커밋 해시를 알 수 없습니다.</b> 빌드가 도커 이미지 안에서 돌아 그 안에 git 정보가 없고,
        넣으려면 배포 파이프라인을 고쳐야 합니다. 대신 위 <b>번들 식별자</b>를 배포 전후로 비교하면
        프론트가 실제로 교체되었는지 알 수 있습니다.
        <br />
        값이 그대로라면 브라우저 캐시일 수 있으니 <b>강력 새로고침</b>(⌘⇧R / Ctrl+Shift+R) 후 다시 확인하세요.
      </p>
    </div>
  );
}
